import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Plus, Trash2, Edit2, Wallet, Receipt, BarChart3, RotateCw, Tag, Receipt as ReceiptIcon, FileSignature, Search, Banknote, Clock } from "lucide-react";
import { ProveedorCombobox } from "@/components/ProveedorCombobox";
import { usePagedList, PaginationBar } from "@/components/Pagination";
import { useAuth } from "@/contexts/AuthContext";
import { FileDown } from "lucide-react";

const MEDIOS_PAGO = [
  { v: "efectivo", l: "Efectivo" },
  { v: "caja_chica", l: "Caja Chica" },
  { v: "transferencia", l: "Transferencia" },
  { v: "cheque", l: "Cheque" },
  { v: "tarjeta", l: "Tarjeta" },
];

export default function GastosOperativos() {
  return (
    <div className="space-y-6" data-testid="gastos-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c7368] mb-1">Contabilidad</div>
        <h1 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>
          Gastos Operativos
        </h1>
        <p className="text-sm text-[#4a5c53] mt-1">Registre gastos por categoría, gestione la caja chica y consulte el reporte mensual.</p>
      </div>

      <Tabs defaultValue="gastos">
        <TabsList className="bg-white border border-[#d1dbd6]">
          <TabsTrigger value="gastos" data-testid="tab-gastos"><Receipt size={14} className="mr-2" />Gastos</TabsTrigger>
          <TabsTrigger value="caja" data-testid="tab-caja-chica"><Wallet size={14} className="mr-2" />Caja Chica</TabsTrigger>
          <TabsTrigger value="categorias" data-testid="tab-categorias"><Tag size={14} className="mr-2" />Categorías</TabsTrigger>
          <TabsTrigger value="reporte" data-testid="tab-reporte"><BarChart3 size={14} className="mr-2" />Reporte mensual</TabsTrigger>
        </TabsList>
        <TabsContent value="gastos"><GastosList /></TabsContent>
        <TabsContent value="caja"><CajaChica /></TabsContent>
        <TabsContent value="categorias"><Categorias /></TabsContent>
        <TabsContent value="reporte"><Reporte /></TabsContent>
      </Tabs>
    </div>
  );
}

// ===================== GASTOS =====================
function GastosList() {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [provs, setProvs] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty());
  const [filterDesde, setFilterDesde] = useState("");
  const [filterHasta, setFilterHasta] = useState("");
  const [filterCat, setFilterCat] = useState("");
  const [filterMedio, setFilterMedio] = useState("");
  const [filterQ, setFilterQ] = useState("");
  const [retencionPrompt, setRetencionPrompt] = useState(null);
  const [retencionDialog, setRetencionDialog] = useState(null);
  const [pagarDialog, setPagarDialog] = useState(null); // gasto que se está pagando
  const [pagarForm, setPagarForm] = useState({ fecha_pago: "", medio_pago: "efectivo", banco: "", numero_cheque: "", numero_transaccion: "", referencia_pago: "", observaciones: "" });
  const pagedGastos = usePagedList(items, 20);

  function empty() {
    const today = new Date().toISOString().slice(0, 10);
    return { fecha: today, categoria_id: "", descripcion: "", subtotal: 0, iva: 0, monto: 0, medio_pago: "efectivo",
             proveedor_id: "", proveedor_nombre: "", documento_referencia: "", autorizacion_sri: "", observaciones: "",
             estado_pago: "pagado", banco: "", numero_cheque: "", numero_transaccion: "", referencia_pago: "", fecha_pago: today };
  }

  const load = async () => {
    const params = new URLSearchParams();
    if (filterDesde) params.set("desde", filterDesde);
    if (filterHasta) params.set("hasta", filterHasta);
    if (filterCat) params.set("categoria_id", filterCat);
    if (filterMedio) params.set("medio_pago", filterMedio);
    if (filterQ && filterQ.trim()) params.set("q", filterQ.trim());
    try {
      const [g, c, p] = await Promise.all([
        api.get(`/gastos?${params.toString()}`),
        api.get("/gastos/categorias"),
        api.get("/masters/proveedores").catch(() => ({ data: [] })),
      ]);
      setItems(g.data); setCats(c.data); setProvs(p.data);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filterDesde, filterHasta, filterCat, filterMedio, filterQ]);

  const save = async () => {
    if (!form.categoria_id) return toast.error("Seleccione categoría");
    if (!form.descripcion) return toast.error("Ingrese descripción");
    if (+form.monto <= 0) return toast.error("Monto debe ser > 0");
    try {
      const payload = { ...form, monto: parseFloat(form.monto), proveedor_id: form.proveedor_id || null };
      let saved;
      if (editing) {
        const r = await api.patch(`/gastos/${editing.id}`, payload);
        saved = r.data;
        toast.success("Gasto actualizado");
      } else {
        const r = await api.post("/gastos", payload);
        saved = r.data;
        toast.success("Gasto registrado");
      }
      setDialogOpen(false); setEditing(null); setForm(empty()); load();
      // ITER164: al CREAR un gasto nuevo, siempre preguntar si realiza retención
      // (aunque no tenga proveedor_id/documento_referencia — el modal informa
      // al usuario si le faltan datos y le da opción de completarlos).
      if (!editing && !saved?.retencion_id) {
        setRetencionPrompt(saved);
      }
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const editar = (g) => {
    setEditing(g);
    setForm({
      fecha: g.fecha,
      categoria_id: g.categoria_id,
      descripcion: g.descripcion,
      subtotal: g.subtotal ?? 0,
      iva: g.iva ?? 0,
      monto: g.monto,
      medio_pago: g.medio_pago,
      proveedor_id: g.proveedor_id || "",
      proveedor_nombre: g.proveedor_nombre || "",
      documento_referencia: g.documento_referencia || "",
      autorizacion_sri: g.autorizacion_sri || "",
      observaciones: g.observaciones || "",
      estado_pago: g.estado_pago || "pagado",
      banco: g.banco || "",
      numero_cheque: g.numero_cheque || "",
      numero_transaccion: g.numero_transaccion || "",
      referencia_pago: g.referencia_pago || "",
      fecha_pago: g.fecha_pago || g.fecha,
    });
    setDialogOpen(true);
  };
  const remove = async (g) => {
    if (!window.confirm(`¿Eliminar gasto "${g.descripcion}" ($${g.monto.toFixed(2)})?`)) return;
    try { await api.delete(`/gastos/${g.id}`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  const abrirPagar = (g) => {
    const today = new Date().toISOString().slice(0, 10);
    setPagarForm({ fecha_pago: today, medio_pago: g.medio_pago || "efectivo", banco: g.banco || "", numero_cheque: g.numero_cheque || "", numero_transaccion: g.numero_transaccion || "", referencia_pago: g.referencia_pago || "", observaciones: "" });
    setPagarDialog(g);
  };
  const confirmarPago = async () => {
    if (!pagarDialog) return;
    try {
      await api.post(`/gastos/${pagarDialog.id}/pagar`, pagarForm);
      toast.success(`Pago registrado para "${pagarDialog.descripcion}"`);
      setPagarDialog(null);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const total = useMemo(() => items.reduce((s, g) => s + (+g.monto || 0), 0), [items]);

  return (
    <div className="mt-4 space-y-4">
      <div className="rounded-md border border-[#d1dbd6] bg-white p-3 grid md:grid-cols-12 gap-2 items-end">
        <div className="md:col-span-2"><Label className="text-xs">Desde</Label><Input type="date" value={filterDesde} onChange={(e) => setFilterDesde(e.target.value)} data-testid="gasto-filter-desde" /></div>
        <div className="md:col-span-2"><Label className="text-xs">Hasta</Label><Input type="date" value={filterHasta} onChange={(e) => setFilterHasta(e.target.value)} data-testid="gasto-filter-hasta" /></div>
        <div className="md:col-span-3">
          <Label className="text-xs">Buscar (descripción · proveedor · RUC · doc)</Label>
          <Input
            value={filterQ}
            onChange={(e) => setFilterQ(e.target.value)}
            placeholder="Ej: luz, 0999999999001, factura 001..."
            data-testid="gasto-filter-q"
          />
        </div>
        <div className="md:col-span-2">
          <Label className="text-xs">Categoría</Label>
          <Select value={filterCat || "todas"} onValueChange={(v) => setFilterCat(v === "todas" ? "" : v)}>
            <SelectTrigger><SelectValue placeholder="Todas" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todas">Todas las categorías</SelectItem>
              {cats.map(c => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-1">
          <Label className="text-xs">Medio</Label>
          <Select value={filterMedio || "todos"} onValueChange={(v) => setFilterMedio(v === "todos" ? "" : v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="todos">Todos</SelectItem>
              {MEDIOS_PAGO.map(m => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="md:col-span-2 flex justify-end">
          <Button onClick={() => { setEditing(null); setForm(empty()); setDialogOpen(true); }} className="bg-[#205c45]" data-testid="create-gasto-btn">
            <Plus size={16} className="mr-2" /> Nuevo gasto
          </Button>
        </div>
      </div>

      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
        <table className="erp-table">
          <thead><tr><th>Fecha</th><th>Categoría</th><th>Descripción</th><th>Proveedor</th><th>Doc ref.</th><th>Medio pago</th><th className="text-right">Monto</th><th>Usuario</th><th className="w-24"></th></tr></thead>
          <tbody>
            {pagedGastos.paged.map(g => {
              const pendiente = g.estado_pago === "pendiente";
              const detallePago = [g.banco, g.numero_cheque && `Cheque ${g.numero_cheque}`, g.numero_transaccion && `Tx ${g.numero_transaccion}`, g.referencia_pago].filter(Boolean).join(" · ");
              return (
              <tr key={g.id} data-testid={`gasto-row-${g.id}`} className={pendiente ? "bg-[#fef7e0]" : ""}>
                <td>{g.fecha}</td>
                <td><Badge variant="outline">{g.categoria_nombre}</Badge></td>
                <td className="font-medium">{g.descripcion}</td>
                <td>{g.proveedor_nombre || "—"}</td>
                <td className="font-mono text-xs">{g.documento_referencia || "—"}</td>
                <td>
                  {pendiente ? (
                    <Badge className="bg-[#fde5b0] text-[#8a5500] border border-[#e5cf99]" data-testid={`gasto-badge-pendiente-${g.id}`}>
                      <Clock size={10} className="mr-1" />PENDIENTE
                    </Badge>
                  ) : g.medio_pago === "caja_chica" ? (
                    <Badge className="bg-[#fef7ec] text-[#8a5500] border border-[#f5c477]">Caja Chica</Badge>
                  ) : (
                    <div className="text-xs">
                      <span className="capitalize">{g.medio_pago}</span>
                      {detallePago && <div className="text-[10px] text-[#5c7368] mt-0.5" title={detallePago}>{detallePago.length > 32 ? detallePago.slice(0, 30) + "…" : detallePago}</div>}
                    </div>
                  )}
                </td>
                <td className="text-right tabular-nums font-semibold">${g.monto.toFixed(2)}</td>
                <td className="text-xs text-[#5c7368]">{g.user_nombre}</td>
                <td>
                  <div className="flex gap-1 justify-end items-center">
                    {pendiente && (
                      <Button size="icon" variant="ghost" title="Registrar pago" onClick={() => abrirPagar(g)} data-testid={`gasto-pagar-${g.id}`}>
                        <Banknote size={14} className="text-[#205c45]" />
                      </Button>
                    )}
                    {g.retencion_numero && (
                      <Link
                        to={`/sri-complementos?tab=retenciones&numero=${encodeURIComponent(g.retencion_numero)}`}
                        title={`Ir a Complementos SRI → Retención ${g.retencion_numero}`}
                        data-testid={`gasto-ret-link-${g.id}`}
                      >
                        <Badge className="bg-[#e9f2ee] text-[#205c45] border border-[#b2d0c2] text-[10px] cursor-pointer hover:bg-[#d2e5db] transition-colors">
                          <FileSignature size={10} className="mr-1" />Ret · {g.retencion_numero}
                        </Badge>
                      </Link>
                    )}
                    {g.proveedor_id && g.documento_referencia && !g.retencion_id && (
                      <Button size="icon" variant="ghost" title="Generar retención SRI" onClick={async () => {
                        try {
                          const { data } = await api.get(`/gastos/${g.id}/sugerir-retencion`);
                          setRetencionDialog({ gasto: g, ...data });
                        } catch (e) { toast.error(formatApiError(e)); }
                      }} data-testid={`gasto-ret-btn-${g.id}`}><FileSignature size={14} className="text-[#8a5500]" /></Button>
                    )}
                    <Button size="icon" variant="ghost" onClick={() => editar(g)}><Edit2 size={14} /></Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(g)}><Trash2 size={14} className="text-[#b53a25]" /></Button>
                  </div>
                </td>
              </tr>
              );
            })}
            {items.length === 0 && <tr><td colSpan={9} className="text-center py-10 text-[#8ba397]">Sin gastos en el período</td></tr>}
          </tbody>
          {items.length > 0 && (
            <tfoot className="bg-[#f7f9f8] border-t">
              <tr><td colSpan={6} className="text-right font-semibold uppercase text-xs tracking-wider text-[#5c7368] px-3 py-2">Total ({items.length})</td>
                  <td className="text-right font-bold text-lg tabular-nums px-3 py-2">${total.toFixed(2)}</td>
                  <td colSpan={2}></td></tr>
            </tfoot>
          )}
        </table>
        <PaginationBar page={pagedGastos.page} total={pagedGastos.total} pageSize={pagedGastos.pageSize} onPrev={pagedGastos.prev} onNext={pagedGastos.next} label="gastos" />
      </div>

      <Dialog open={dialogOpen} onOpenChange={(v) => { setDialogOpen(v); if (!v) setEditing(null); }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader><DialogTitle>{editing ? "Editar gasto" : "Nuevo gasto"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Fecha</Label><Input type="date" value={form.fecha} onChange={(e) => setForm({ ...form, fecha: e.target.value })} data-testid="gasto-fecha" /></div>
            <div>
              <Label>Categoría</Label>
              <Select value={form.categoria_id} onValueChange={(v) => setForm({ ...form, categoria_id: v })}>
                <SelectTrigger data-testid="gasto-categoria"><SelectValue placeholder="..." /></SelectTrigger>
                <SelectContent>{cats.map(c => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Descripción</Label><Input value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} placeholder="Ej: Pago de luz mes mayo" data-testid="gasto-descripcion" /></div>

            {/* Bloque de montos */}
            <div className="col-span-2 rounded-md border border-[#d1dbd6] bg-[#f7f9f8] p-3">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-[#5c7368] mb-2">Montos del comprobante</div>
              <div className="grid grid-cols-4 gap-2 items-end">
                <div>
                  <Label className="text-xs">Subtotal (base imponible)</Label>
                  <Input
                    type="number" step="0.01" min="0"
                    value={form.subtotal}
                    onChange={(e) => {
                      const sub = parseFloat(e.target.value) || 0;
                      setForm(f => ({ ...f, subtotal: e.target.value, monto: +(sub + (parseFloat(f.iva) || 0)).toFixed(2) }));
                    }}
                    data-testid="gasto-subtotal"
                    className="text-right tabular-nums"
                  />
                </div>
                <div>
                  <Label className="text-xs">+ IVA</Label>
                  <Input
                    type="number" step="0.01" min="0"
                    value={form.iva}
                    onChange={(e) => {
                      const iva = parseFloat(e.target.value) || 0;
                      setForm(f => ({ ...f, iva: e.target.value, monto: +((parseFloat(f.subtotal) || 0) + iva).toFixed(2) }));
                    }}
                    data-testid="gasto-iva"
                    className="text-right tabular-nums"
                  />
                </div>
                <div>
                  <Label className="text-xs">% IVA rápido</Label>
                  <Select value={"manual"} onValueChange={(v) => {
                    const sub = parseFloat(form.subtotal) || 0;
                    const pct = v === "manual" ? null : parseFloat(v);
                    if (pct == null) return;
                    const iva = +(sub * pct / 100).toFixed(2);
                    setForm(f => ({ ...f, iva, monto: +(sub + iva).toFixed(2) }));
                  }}>
                    <SelectTrigger className="h-9"><SelectValue placeholder="Manual" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="manual">— Manual —</SelectItem>
                      <SelectItem value="0">0%</SelectItem>
                      <SelectItem value="5">5%</SelectItem>
                      <SelectItem value="12">12%</SelectItem>
                      <SelectItem value="15">15%</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs font-bold">= TOTAL</Label>
                  <Input
                    type="number" step="0.01" min="0"
                    value={form.monto}
                    onChange={(e) => setForm({ ...form, monto: e.target.value })}
                    data-testid="gasto-monto"
                    className="text-right tabular-nums text-base font-bold h-10 border-[#205c45]"
                  />
                </div>
              </div>
              <p className="text-[10px] text-[#5c7368] italic mt-1">El subtotal se usa como base imponible al sugerir la retención IR. Puede editar el total a mano si difiere.</p>
            </div>

            {/* ITER167: Estado de pago y detalles del medio (como CxP) */}
            <div className="col-span-2 rounded-md border border-[#c7d3cc] bg-white p-3 space-y-3">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-[#5c7368]">Pago</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Estado del pago</Label>
                  <Select value={form.estado_pago} onValueChange={(v) => setForm({ ...form, estado_pago: v })}>
                    <SelectTrigger data-testid="gasto-estado-pago"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pagado">Pagado</SelectItem>
                      <SelectItem value="pendiente">Pendiente (pagar después)</SelectItem>
                    </SelectContent>
                  </Select>
                  {form.estado_pago === "pendiente" && (
                    <p className="text-[10px] text-[#8a5500] mt-1">
                      Se registrará como obligación por pagar. No se descontará de Caja Chica ni se generará asiento hasta registrar el pago.
                    </p>
                  )}
                </div>
                <div>
                  <Label>Medio de pago</Label>
                  <Select value={form.medio_pago} onValueChange={(v) => setForm({ ...form, medio_pago: v })}>
                    <SelectTrigger data-testid="gasto-medio-pago"><SelectValue /></SelectTrigger>
                    <SelectContent>{MEDIOS_PAGO.map(m => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              {/* Detalles del medio de pago (solo si es pagado y el medio los amerita) */}
              {form.estado_pago === "pagado" && (form.medio_pago === "cheque" || form.medio_pago === "transferencia" || form.medio_pago === "tarjeta") && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Banco</Label>
                    <Input value={form.banco} onChange={(e) => setForm({ ...form, banco: e.target.value })} placeholder="Ej: Banco Pichincha" data-testid="gasto-banco" />
                  </div>
                  {form.medio_pago === "cheque" && (
                    <div>
                      <Label className="text-xs">N° Cheque</Label>
                      <Input value={form.numero_cheque} onChange={(e) => setForm({ ...form, numero_cheque: e.target.value })} placeholder="Ej: 001234" data-testid="gasto-num-cheque" />
                    </div>
                  )}
                  {form.medio_pago === "transferencia" && (
                    <div>
                      <Label className="text-xs">N° Transacción</Label>
                      <Input value={form.numero_transaccion} onChange={(e) => setForm({ ...form, numero_transaccion: e.target.value })} placeholder="Nº transferencia bancaria" data-testid="gasto-num-transaccion" />
                    </div>
                  )}
                  {form.medio_pago === "tarjeta" && (
                    <div>
                      <Label className="text-xs">N° Voucher / Autorización</Label>
                      <Input value={form.numero_transaccion} onChange={(e) => setForm({ ...form, numero_transaccion: e.target.value })} placeholder="Voucher / cód. autorización" data-testid="gasto-num-voucher" />
                    </div>
                  )}
                  <div className="col-span-2">
                    <Label className="text-xs">Referencia / Observación del pago</Label>
                    <Input value={form.referencia_pago} onChange={(e) => setForm({ ...form, referencia_pago: e.target.value })} placeholder="Ej: pago servicios febrero" data-testid="gasto-ref-pago" />
                  </div>
                </div>
              )}
              {form.estado_pago === "pagado" && (
                <div>
                  <Label className="text-xs">Fecha de pago</Label>
                  <Input type="date" value={form.fecha_pago} onChange={(e) => setForm({ ...form, fecha_pago: e.target.value })} data-testid="gasto-fecha-pago" />
                </div>
              )}
            </div>
            <div>
              <Label>Documento ref. (Nº factura/recibo)</Label>
              <Input value={form.documento_referencia} onChange={(e) => setForm({ ...form, documento_referencia: e.target.value })} placeholder="001-001-000123456" />
            </div>
            <div>
              <Label>Proveedor del catálogo (con RUC, requerido para retención SRI)</Label>
              <ProveedorCombobox
                value={form.proveedor_id}
                onChange={(v) => {
                  const p = provs.find(x => x.id === v);
                  setForm(f => ({ ...f, proveedor_id: v, proveedor_nombre: p?.razon_social || "" }));
                }}
                proveedores={provs}
                testId="gasto-proveedor-cb"
              />
            </div>
            <div>
              <Label>Beneficiario (texto libre, si no está en catálogo)</Label>
              <Input value={form.proveedor_nombre} onChange={(e) => setForm({ ...form, proveedor_nombre: e.target.value })} disabled={!!form.proveedor_id} />
            </div>
            <div className="col-span-2">
              <Label>Autorización SRI (clave de acceso, opcional)</Label>
              <Input
                value={form.autorizacion_sri}
                onChange={(e) => setForm({ ...form, autorizacion_sri: e.target.value.replace(/\s/g, "").slice(0, 49) })}
                maxLength={49}
                placeholder="49 dígitos de la clave de acceso del comprobante recibido"
              />
              <div className="text-[10px] text-[#5c7368] mt-0.5 tabular-nums">
                {(form.autorizacion_sri || "").length}/49
                {form.autorizacion_sri && form.autorizacion_sri.length > 0 && form.autorizacion_sri.length < 49 && (
                  <span className="text-[#b53a25] ml-1">· faltan {49 - form.autorizacion_sri.length}</span>
                )}
              </div>
            </div>
            <div className="col-span-2"><Label>Observaciones</Label><Input value={form.observaciones} onChange={(e) => setForm({ ...form, observaciones: e.target.value })} /></div>

            <div className="col-span-2 rounded-md border border-[#b2d0c2] bg-[#e9f2ee] p-2 text-[11px] text-[#205c45]">
              💡 Al guardar, si el gasto tiene <strong>proveedor del catálogo</strong> + <strong>documento de referencia</strong>, el sistema le ofrecerá generar la retención SRI automáticamente. También puede generarla desde el botón ✍︎ (firma) en la fila del listado.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={save} className="bg-[#205c45]" data-testid="save-gasto-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ITER167 · Diálogo para pagar un gasto pendiente */}
      <Dialog open={!!pagarDialog} onOpenChange={(o) => !o && setPagarDialog(null)}>
        <DialogContent className="max-w-lg" data-testid="pagar-gasto-dialog">
          <DialogHeader><DialogTitle>Registrar pago del gasto</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="rounded-md bg-[#f7f9f8] border border-[#d1dbd6] p-2 text-xs">
              <div><strong>{pagarDialog?.descripcion}</strong></div>
              <div className="text-[#5c7368] mt-0.5">
                {pagarDialog?.proveedor_nombre || "sin proveedor"} · {pagarDialog?.documento_referencia || "sin comprobante"} · Total <span className="font-semibold tabular-nums">${pagarDialog?.monto?.toFixed(2)}</span>
                {pagarDialog?.retencion_total > 0 && (
                  <span className="ml-2 text-[#205c45]">· Retención aplicada <span className="tabular-nums">${(+pagarDialog.retencion_total).toFixed(2)}</span></span>
                )}
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Fecha de pago</Label>
                <Input type="date" value={pagarForm.fecha_pago} onChange={(e) => setPagarForm(f => ({ ...f, fecha_pago: e.target.value }))} data-testid="pagar-fecha" />
              </div>
              <div>
                <Label className="text-xs">Medio de pago</Label>
                <Select value={pagarForm.medio_pago} onValueChange={(v) => setPagarForm(f => ({ ...f, medio_pago: v }))}>
                  <SelectTrigger data-testid="pagar-medio"><SelectValue /></SelectTrigger>
                  <SelectContent>{MEDIOS_PAGO.map(m => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            {(pagarForm.medio_pago === "cheque" || pagarForm.medio_pago === "transferencia" || pagarForm.medio_pago === "tarjeta") && (
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Banco</Label>
                  <Input value={pagarForm.banco} onChange={(e) => setPagarForm(f => ({ ...f, banco: e.target.value }))} placeholder="Ej: Banco Pichincha" data-testid="pagar-banco" />
                </div>
                {pagarForm.medio_pago === "cheque" && (
                  <div>
                    <Label className="text-xs">N° Cheque</Label>
                    <Input value={pagarForm.numero_cheque} onChange={(e) => setPagarForm(f => ({ ...f, numero_cheque: e.target.value }))} data-testid="pagar-cheque" />
                  </div>
                )}
                {pagarForm.medio_pago === "transferencia" && (
                  <div>
                    <Label className="text-xs">N° Transacción</Label>
                    <Input value={pagarForm.numero_transaccion} onChange={(e) => setPagarForm(f => ({ ...f, numero_transaccion: e.target.value }))} data-testid="pagar-transaccion" />
                  </div>
                )}
                {pagarForm.medio_pago === "tarjeta" && (
                  <div>
                    <Label className="text-xs">Voucher / Autorización</Label>
                    <Input value={pagarForm.numero_transaccion} onChange={(e) => setPagarForm(f => ({ ...f, numero_transaccion: e.target.value }))} data-testid="pagar-voucher" />
                  </div>
                )}
              </div>
            )}
            <div>
              <Label className="text-xs">Referencia / Observación del pago (opcional)</Label>
              <Input value={pagarForm.referencia_pago} onChange={(e) => setPagarForm(f => ({ ...f, referencia_pago: e.target.value }))} placeholder="Ej: pago después de retención" data-testid="pagar-ref" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPagarDialog(null)}>Cancelar</Button>
            <Button onClick={confirmarPago} className="bg-[#205c45]" data-testid="pagar-confirm-btn">
              <Banknote size={14} className="mr-2" />Registrar pago
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Prompt automático: ¿Generar retención SRI? */}
      <Dialog open={!!retencionPrompt} onOpenChange={(o) => !o && setRetencionPrompt(null)}>
        <DialogContent className="max-w-md" data-testid="retencion-prompt-gasto">
          <DialogHeader><DialogTitle>¿Generar retención SRI?</DialogTitle></DialogHeader>
          <div className="text-sm text-[#4a5c53] space-y-2">
            <p>
              El gasto <strong>{retencionPrompt?.descripcion || "sin descripción"}</strong>
              {retencionPrompt?.proveedor_nombre ? (
                <> de <strong>{retencionPrompt.proveedor_nombre}</strong>{retencionPrompt?.proveedor_ruc ? <> (RUC {retencionPrompt.proveedor_ruc})</> : null}</>
              ) : (
                <> <span className="italic text-[#8ba397]">(sin proveedor asignado)</span></>
              )}
              {" "}por <span className="tabular-nums font-semibold">${retencionPrompt?.monto?.toFixed(2)}</span>
              {retencionPrompt?.documento_referencia ? (
                <> con comprobante <span className="font-mono">{retencionPrompt.documento_referencia}</span></>
              ) : null}
              {" "}fue registrado.
            </p>
            {(!retencionPrompt?.proveedor_id || !retencionPrompt?.documento_referencia) && (
              <p className="text-xs text-[#b08400] bg-[#fef7e0] border border-[#e5cf99] rounded p-2">
                ⚠ Para generar la retención SRI necesita completar antes {(!retencionPrompt?.proveedor_id ? "el proveedor" : "")}{(!retencionPrompt?.proveedor_id && !retencionPrompt?.documento_referencia) ? " y " : ""}{(!retencionPrompt?.documento_referencia ? "el número de comprobante" : "")}. Puede hacerlo editando el gasto.
              </p>
            )}
            <p>Si su empresa es agente de retención puede generar el comprobante SRI ahora.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRetencionPrompt(null)} data-testid="ret-prompt-skip">Más tarde</Button>
            <Button className="bg-[#205c45]" onClick={async () => {
              const g = retencionPrompt;
              setRetencionPrompt(null);
              try {
                const { data } = await api.get(`/gastos/${g.id}/sugerir-retencion`);
                setRetencionDialog({ gasto: g, ...data });
              } catch (e) { toast.error(formatApiError(e)); }
            }} data-testid="ret-prompt-now">Generar retención</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Diálogo retención (selección de líneas y confirmación) */}
      <RetencionDialog data={retencionDialog} setData={setRetencionDialog} onDone={load} />
    </div>
  );
}

// =========== Modal de retención SRI sobre gasto operativo ===========
function RetencionDialog({ data, setData, onDone }) {
  const [lineas, setLineas] = useState([]);
  const [catalogo, setCatalogo] = useState([]);
  const [pickerTipo, setPickerTipo] = useState("renta");
  const [pickerCodigo, setPickerCodigo] = useState("");
  const [fechaRet, setFechaRet] = useState(new Date().toISOString().slice(0, 10)); // ITER207
  // Cargar catálogo SRI una vez al abrir el modal
  useEffect(() => {
    if (!data) return;
    (async () => {
      try {
        const { data: cat } = await api.get("/tipos-retencion");
        setCatalogo(cat || []);
      } catch (e) { console.warn("catalogo retenciones", e); }
    })();
  }, [data]);
  useEffect(() => {
    if (data?.sugerencias) {
      setLineas(data.sugerencias.map(s => ({
        tipo: s.tipo, codigo_sri: s.codigo_sri, descripcion: s.descripcion,
        base_imponible: s.base, porcentaje: s.porcentaje, valor_retenido: s.valor,
        _seleccionada: true, // si vienen del default del proveedor, ya están listas
      })));
    } else { setLineas([]); }
  }, [data]);
  if (!data) return null;
  const close = () => { setData(null); setPickerCodigo(""); };
  const total = lineas.filter(l => l._seleccionada).reduce((s, l) => s + (parseFloat(l.valor_retenido) || 0), 0);

  const baseSubtotal = data.gasto.monto || 0;
  const baseIva = data.gasto.iva || 0;

  const opcionesPicker = catalogo.filter(c => c.tipo === pickerTipo);

  const agregarLinea = () => {
    const t = catalogo.find(c => c.id === pickerCodigo || c.codigo_sri === pickerCodigo);
    if (!t) return toast.error("Seleccione un tipo de retención del catálogo");
    const base = t.tipo === "iva" ? baseIva : baseSubtotal;
    if (t.tipo === "iva" && baseIva <= 0) {
      toast.warning("Este gasto no tiene IVA. Edite primero el gasto para registrar IVA antes de retener.");
    }
    const ya = lineas.findIndex(l => l.codigo_sri === t.codigo_sri && l.tipo === t.tipo);
    if (ya >= 0) {
      // Si ya estaba, solo selecciona
      setLineas(ls => { const n = [...ls]; n[ya] = { ...n[ya], _seleccionada: true }; return n; });
    } else {
      setLineas(ls => [...ls, {
        tipo: t.tipo, codigo_sri: t.codigo_sri, descripcion: t.descripcion,
        base_imponible: base, porcentaje: t.porcentaje,
        valor_retenido: +(base * (t.porcentaje || 0) / 100).toFixed(2),
        _seleccionada: true,
      }]);
    }
    setPickerCodigo("");
  };

  const recalcLinea = (i, patch) => setLineas(ls => {
    const n = [...ls];
    const merged = { ...n[i], ...patch };
    const b = parseFloat(merged.base_imponible) || 0;
    const p = parseFloat(merged.porcentaje) || 0;
    merged.valor_retenido = +(b * p / 100).toFixed(2);
    n[i] = merged;
    return n;
  });

  const eliminarLinea = (i) => setLineas(ls => ls.filter((_, idx) => idx !== i));

  const generar = async () => {
    if (!fechaRet) return toast.error("Indique la fecha de la retención");
    const sel = lineas.filter(l => l._seleccionada).map(({ _seleccionada, ...rest }) => ({
      ...rest,
      base_imponible: parseFloat(rest.base_imponible) || 0,
      porcentaje: parseFloat(rest.porcentaje) || 0,
      valor_retenido: parseFloat(rest.valor_retenido) || 0,
    }));
    if (sel.length === 0) return toast.error("Seleccione al menos una línea");
    try {
      await api.post(`/gastos/${data.gasto.id}/generar-retencion`, { lineas: sel, fecha: fechaRet });
      toast.success(`Retención generada con fecha ${fechaRet}`);
      close(); onDone();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Dialog open={!!data} onOpenChange={(v) => !v && close()}>
      <DialogContent className="max-w-3xl" data-testid="retencion-dialog-gasto">
        <DialogHeader><DialogTitle>Retención SRI · {data.gasto.descripcion}</DialogTitle></DialogHeader>
        <div className="mb-3 grid md:grid-cols-2 gap-3 items-end">
          <div>
            <Label className="text-[10px] uppercase tracking-wider">Fecha de la retención *</Label>
            <Input type="date" value={fechaRet} onChange={(e) => setFechaRet(e.target.value)}
              className="h-9" data-testid="ret-fecha-gasto" />
            <div className="text-[11px] text-[#5c7368] mt-1">
              Fecha de emisión del comprobante (normalmente el día en que se ingresa la factura).
              Fecha del documento del proveedor: <strong>{data.gasto.fecha || "—"}</strong>.
            </div>
          </div>
          {fechaRet && data.gasto.fecha && fechaRet !== data.gasto.fecha && (
            <div className="text-[11px] rounded border border-[#f5c477] bg-[#fef7ec] px-2 py-1.5">
              La retención se emitirá con fecha <strong>{fechaRet}</strong> y el período fiscal
              se calculará con esa fecha, aunque la factura sea del {data.gasto.fecha}.
            </div>
          )}
        </div>
        <div className="text-xs text-[#5c7368] mb-2 flex items-center flex-wrap gap-x-3 gap-y-1">
          <span>Proveedor: <strong>{data.proveedor.razon_social}</strong> (RUC {data.proveedor.ruc})</span>
          <span>· Comprobante: <span className="font-mono">{data.gasto.documento_referencia}</span></span>
          <span>· Base IR: ${baseSubtotal.toFixed(2)}</span>
          <span>· Base IVA: ${baseIva.toFixed(2)}</span>
          {data.proveedor.tiene_default && (
            <Badge className="bg-[#e9f2ee] text-[#205c45] border border-[#b2d0c2] text-[10px]">
              Default proveedor aplicado
            </Badge>
          )}
        </div>

        {/* Línea de agregar tipo del catálogo */}
        <div className="border border-dashed border-[#d1dbd6] rounded-md p-3 mb-3 bg-[#f7f9f8]">
          <div className="text-[10px] uppercase tracking-wider font-semibold text-[#5c7368] mb-2">
            Agregar otra retención del catálogo SRI
          </div>
          <div className="grid grid-cols-12 gap-2">
            <div className="col-span-3">
              <Select value={pickerTipo} onValueChange={(v) => { setPickerTipo(v); setPickerCodigo(""); }}>
                <SelectTrigger data-testid="ret-picker-tipo"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="renta">Renta (IR)</SelectItem>
                  <SelectItem value="iva">IVA</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-7">
              <Select value={pickerCodigo} onValueChange={setPickerCodigo}>
                <SelectTrigger data-testid="ret-picker-codigo">
                  <SelectValue placeholder={`Elija código ${pickerTipo === "iva" ? "IVA" : "IR"}…`} />
                </SelectTrigger>
                <SelectContent>
                  {opcionesPicker.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      <span className="font-mono">{c.codigo_sri}</span> · {c.porcentaje}% — {c.descripcion}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="col-span-2">
              <Button onClick={agregarLinea} disabled={!pickerCodigo} className="w-full bg-[#205c45]" data-testid="ret-add-line-btn">
                <Plus size={14} className="mr-1" /> Agregar
              </Button>
            </div>
          </div>
        </div>

        <table className="erp-table">
          <thead><tr><th></th><th>Tipo</th><th>Código</th><th>Descripción</th><th className="text-right w-24">Base</th><th className="text-right w-16">%</th><th className="text-right w-24">Valor</th><th className="w-8"></th></tr></thead>
          <tbody>
            {lineas.map((l, i) => (
              <tr key={`${l.tipo}-${l.codigo_sri}-${i}`} className={l._seleccionada ? "bg-[#e9f2ee]" : ""}>
                <td><input type="checkbox" checked={l._seleccionada} onChange={(e) => recalcLinea(i, { _seleccionada: e.target.checked })} data-testid={`ret-sel-${i}`} /></td>
                <td className="capitalize"><Badge className="bg-[#fef7ec] text-[#8a5500]">{l.tipo}</Badge></td>
                <td className="font-mono text-xs">{l.codigo_sri}</td>
                <td className="text-xs">{l.descripcion}</td>
                <td className="text-right">
                  <Input type="number" step="0.01" min="0" value={l.base_imponible}
                         onChange={(e) => recalcLinea(i, { base_imponible: e.target.value })}
                         className="h-7 text-xs tabular-nums text-right px-1" data-testid={`ret-base-${i}`} />
                </td>
                <td className="text-right">
                  <Input type="number" step="0.01" min="0" max="100" value={l.porcentaje}
                         onChange={(e) => recalcLinea(i, { porcentaje: e.target.value })}
                         className="h-7 text-xs tabular-nums text-right px-1" data-testid={`ret-pct-${i}`} />
                </td>
                <td className="text-right tabular-nums font-semibold">${(parseFloat(l.valor_retenido) || 0).toFixed(2)}</td>
                <td>
                  <Button size="icon" variant="ghost" onClick={() => eliminarLinea(i)} data-testid={`ret-del-${i}`}>
                    <Trash2 size={12} className="text-[#b53a25]" />
                  </Button>
                </td>
              </tr>
            ))}
            {lineas.length === 0 && (
              <tr><td colSpan={8} className="text-center py-4 text-[#8ba397] italic">
                Sin líneas. Use el selector arriba para agregar retenciones del catálogo SRI.
              </td></tr>
            )}
          </tbody>
          <tfoot><tr><td colSpan={6} className="text-right font-semibold">Total a retener</td><td className="text-right font-bold tabular-nums">${total.toFixed(2)}</td><td></td></tr></tfoot>
        </table>
        <p className="text-[11px] text-[#5c7368] mt-2">
          <strong>Tip:</strong> configure las retenciones habituales por proveedor en{" "}
          <a href={`/proveedores`} className="text-[#205c45] font-semibold underline" target="_blank" rel="noreferrer">
            Maestros → Proveedores
          </a>{" "}para que se sugieran automáticamente.
        </p>
        <DialogFooter>
          <Button variant="outline" onClick={close}>Cancelar</Button>
          <Button onClick={generar} className="bg-[#205c45]" data-testid="ret-confirm-gasto"><FileSignature size={14} className="mr-2" />Generar retención</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ===================== CAJA CHICA =====================
function CajaChica() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [estado, setEstado] = useState(null);
  const [movs, setMovs] = useState([]);
  const [configOpen, setConfigOpen] = useState(false);
  const [reposOpen, setReposOpen] = useState(false);
  const [fondoNuevo, setFondoNuevo] = useState(0);
  const [reposForm, setReposForm] = useState({ monto: "", medio_pago_origen: "transferencia", observaciones: "" });
  const [reporteOpen, setReporteOpen] = useState(false);
  const today = new Date();
  const [repYear, setRepYear] = useState(today.getFullYear());
  const [repMonth, setRepMonth] = useState(today.getMonth() + 1);
  const [reporte, setReporte] = useState(null);
  const [loadingReporte, setLoadingReporte] = useState(false);

  const load = async () => {
    try {
      const [e, m] = await Promise.all([
        api.get("/gastos/caja-chica/estado"),
        api.get("/gastos/caja-chica/movimientos"),
      ]);
      setEstado(e.data); setMovs(m.data);
      setFondoNuevo(e.data?.fondo_total || 250);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const guardarFondo = async () => {
    try {
      await api.put("/gastos/caja-chica/config", { fondo_total: parseFloat(fondoNuevo) || 0 });
      toast.success("Fondo actualizado");
      setConfigOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const reponer = async () => {
    try {
      const payload = {
        monto: reposForm.monto ? parseFloat(reposForm.monto) : null,
        medio_pago_origen: reposForm.medio_pago_origen,
        observaciones: reposForm.observaciones || null,
      };
      const { data } = await api.post("/gastos/caja-chica/reposicion", payload);
      toast.success(`Reposición $${data.reposicion.monto.toFixed(2)} · saldo nuevo $${data.saldo_nuevo.saldo.toFixed(2)}`);
      setReposOpen(false); setReposForm({ monto: "", medio_pago_origen: "transferencia", observaciones: "" });
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const [reposEditOpen, setReposEditOpen] = useState(null); // reposicion being edited (admin)
  const [reposEditForm, setReposEditForm] = useState({ monto: "", observaciones: "", medio_pago_origen: "transferencia" });

  const cargarReporte = async () => {
    setLoadingReporte(true);
    try {
      const { data } = await api.get(`/gastos/caja-chica/reporte-mensual?year=${repYear}&month=${repMonth}`);
      setReporte(data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoadingReporte(false); }
  };

  const descargarPdfReporte = async () => {
    try {
      const r = await api.get(`/gastos/caja-chica/reporte-mensual/pdf?year=${repYear}&month=${repMonth}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `caja_chica_${repYear}_${String(repMonth).padStart(2, "0")}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const abrirEditReposicion = (r) => {
    setReposEditOpen(r);
    setReposEditForm({
      monto: r.monto,
      observaciones: r.observaciones || "",
      medio_pago_origen: r.medio_pago_origen || "transferencia",
    });
  };

  const guardarEditReposicion = async () => {
    if (!reposEditOpen) return;
    try {
      await api.patch(`/gastos/caja-chica/reposicion/${reposEditOpen.id}`, {
        monto: parseFloat(reposEditForm.monto) || 0,
        observaciones: reposEditForm.observaciones,
        medio_pago_origen: reposEditForm.medio_pago_origen,
      });
      toast.success("Reposición actualizada");
      setReposEditOpen(null);
      load();
      if (reporte) cargarReporte();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (!estado) return <div className="mt-4 text-sm text-[#5c7368]">Cargando…</div>;
  const pct = estado.porcentaje_disponible || 0;
  const colorBar = pct < 25 ? "bg-[#b53a25]" : pct < 50 ? "bg-[#f5c477]" : "bg-[#205c45]";

  return (
    <div className="mt-4 space-y-4">
      <div className="grid md:grid-cols-4 gap-3">
        <div className="rounded-md bg-white border border-[#d1dbd6] p-4">
          <div className="text-xs uppercase tracking-wider text-[#5c7368] font-semibold">Fondo total</div>
          <div className="text-3xl font-bold text-[#15241d] mt-1 tabular-nums">${estado.fondo_total.toFixed(2)}</div>
          <Button size="sm" variant="outline" className="mt-2" onClick={() => setConfigOpen(true)} data-testid="caja-config-btn">Configurar</Button>
        </div>
        <div className="rounded-md bg-white border border-[#d1dbd6] p-4">
          <div className="text-xs uppercase tracking-wider text-[#5c7368] font-semibold">Saldo disponible</div>
          <div className="text-3xl font-bold text-[#205c45] mt-1 tabular-nums" data-testid="caja-saldo">${estado.saldo.toFixed(2)}</div>
          <div className="mt-2 h-2 w-full bg-[#e6ebe8] rounded-full overflow-hidden">
            <div className={`h-full ${colorBar} transition-all`} style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} />
          </div>
          <div className="text-[10px] text-[#5c7368] mt-1">{pct.toFixed(1)}% del fondo</div>
        </div>
        <div className="rounded-md bg-white border border-[#d1dbd6] p-4">
          <div className="text-xs uppercase tracking-wider text-[#5c7368] font-semibold">Total repuesto</div>
          <div className="text-3xl font-bold text-[#15241d] mt-1 tabular-nums">${estado.total_repuesto.toFixed(2)}</div>
        </div>
        <div className="rounded-md bg-white border border-[#d1dbd6] p-4">
          <div className="text-xs uppercase tracking-wider text-[#5c7368] font-semibold">Total gastado</div>
          <div className="text-3xl font-bold text-[#b53a25] mt-1 tabular-nums">${estado.total_gastado.toFixed(2)}</div>
        </div>
      </div>

      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={() => { cargarReporte(); setReporteOpen(true); }} data-testid="reporte-caja-btn">
          <BarChart3 size={16} className="mr-2" /> Reporte mensual
        </Button>
        <Button onClick={() => setReposOpen(true)} className="bg-[#205c45]" data-testid="reponer-caja-btn">
          <RotateCw size={16} className="mr-2" /> Reponer caja chica
        </Button>
      </div>

      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
        <table className="erp-table">
          <thead><tr><th>Fecha</th><th>Tipo</th><th>Descripción</th><th>Detalle</th><th className="text-right">Monto</th><th>Usuario</th>{isAdmin && <th className="w-10"></th>}</tr></thead>
          <tbody>
            {movs.map((m, i) => (
              <tr key={i} data-testid={`caja-mov-${i}`}>
                <td className="font-mono text-xs">{m.fecha}</td>
                <td>
                  {m.tipo === "reposicion" ? (
                    <Badge className="bg-[#e9f2ee] text-[#205c45] border border-[#b2d0c2]">+ Reposición</Badge>
                  ) : (
                    <Badge className="bg-[#fdecea] text-[#b53a25] border border-[#d99584]">- Gasto</Badge>
                  )}
                </td>
                <td>{m.descripcion || (m.tipo === "reposicion" ? "Reposición de fondo" : "—")}</td>
                <td className="text-xs text-[#5c7368]">{m.tipo === "reposicion" ? `Origen: ${m.medio_pago_origen}` : (m.categoria_nombre || "")}{m.observaciones ? ` · ${m.observaciones}` : ""}</td>
                <td className={`text-right tabular-nums font-semibold ${m.tipo === "reposicion" ? "text-[#205c45]" : "text-[#b53a25]"}`}>
                  {m.signo}${(+m.monto).toFixed(2)}
                </td>
                <td className="text-xs text-[#5c7368]">{m.user_nombre}</td>
                {isAdmin && (
                  <td className="text-right">
                    {m.tipo === "reposicion" && (
                      <Button
                        size="icon"
                        variant="ghost"
                        onClick={() => abrirEditReposicion(m)}
                        title="Editar reposición (admin)"
                        className="h-7 w-7"
                        data-testid={`caja-edit-repos-${m.id}`}
                      >
                        <Edit2 size={13} className="text-[#205c45]" />
                      </Button>
                    )}
                  </td>
                )}
              </tr>
            ))}
            {movs.length === 0 && <tr><td colSpan={isAdmin ? 7 : 6} className="text-center py-10 text-[#8ba397]">Sin movimientos. Realice una reposición para empezar.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Config dialog */}
      <Dialog open={configOpen} onOpenChange={setConfigOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Configurar fondo Caja Chica</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Label>Monto fijo del fondo (USD)</Label>
            <Input type="number" step="0.01" value={fondoNuevo} onChange={(e) => setFondoNuevo(e.target.value)} data-testid="caja-fondo-input" />
            <p className="text-xs text-[#5c7368] italic">Default sugerido: $250. Cuando el saldo baje, podrá reponer hasta este monto.</p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfigOpen(false)}>Cancelar</Button>
            <Button onClick={guardarFondo} className="bg-[#205c45]" data-testid="caja-config-save">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reposición dialog */}
      <Dialog open={reposOpen} onOpenChange={setReposOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reponer Caja Chica</DialogTitle>
            <DialogDescription>El monto máximo a reponer se limita al fondo configurado.</DialogDescription>
          </DialogHeader>
          {(() => {
            const fondo = Number(estado?.fondo_total) || 0;
            const saldo = Number(estado?.saldo) || 0;
            const maxPerm = Math.max(0, +(fondo - saldo).toFixed(2));
            const montoInput = parseFloat(reposForm.monto) || 0;
            const excede = montoInput > 0 && montoInput > maxPerm + 0.01;
            return (
              <div className="space-y-3">
                <div className="rounded-md bg-[#e9f2ee] border border-[#b2d0c2] p-3 text-xs text-[#205c45]">
                  <div>Fondo configurado: <b>${fondo.toFixed(2)}</b></div>
                  <div>Saldo actual: <b>${saldo.toFixed(2)}</b></div>
                  <div>Máximo a reponer: <b>${maxPerm.toFixed(2)}</b></div>
                </div>
                <div>
                  <Label>Monto a reponer <span className="text-[10px] text-[#5c7368]">(vacío = completar al fondo)</span></Label>
                  <Input
                    type="number"
                    step="0.01"
                    max={maxPerm}
                    value={reposForm.monto}
                    onChange={(e) => setReposForm(f => ({ ...f, monto: e.target.value }))}
                    placeholder={`Máx $${maxPerm.toFixed(2)}`}
                    data-testid="repos-monto"
                    className={excede ? "border-rose-400 focus-visible:ring-rose-300" : ""}
                  />
                  {excede && (
                    <p className="text-[11px] text-rose-700 mt-1 font-medium" data-testid="repos-exceso-alerta">
                      ⚠ Supera el fondo configurado. Solicite al administrador aumentar el fondo antes de reponer más.
                    </p>
                  )}
                </div>
                <div>
                  <Label>Origen del fondo (medio de pago)</Label>
                  <Select value={reposForm.medio_pago_origen} onValueChange={(v) => setReposForm(f => ({ ...f, medio_pago_origen: v }))}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="transferencia">Transferencia bancaria</SelectItem>
                      <SelectItem value="efectivo">Efectivo</SelectItem>
                      <SelectItem value="cheque">Cheque</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div><Label>Observaciones</Label><Input value={reposForm.observaciones} onChange={(e) => setReposForm(f => ({ ...f, observaciones: e.target.value }))} /></div>
              </div>
            );
          })()}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReposOpen(false)}>Cancelar</Button>
            <Button
              onClick={reponer}
              disabled={(() => {
                const fondo = Number(estado?.fondo_total) || 0;
                const saldo = Number(estado?.saldo) || 0;
                const maxPerm = Math.max(0, +(fondo - saldo).toFixed(2));
                const montoInput = parseFloat(reposForm.monto) || 0;
                return montoInput > 0 && montoInput > maxPerm + 0.01;
              })()}
              className="bg-[#205c45]"
              data-testid="repos-confirm"
            >
              Reponer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Editar reposición (solo admin) */}
      <Dialog open={!!reposEditOpen} onOpenChange={(v) => { if (!v) setReposEditOpen(null); }}>
        <DialogContent className="max-w-md" data-testid="repos-edit-dialog">
          <DialogHeader>
            <DialogTitle>Editar reposición</DialogTitle>
            <DialogDescription>Solo administradores. Ajuste el monto si se registró un valor incorrecto.</DialogDescription>
          </DialogHeader>
          {reposEditOpen && (
            <div className="space-y-3">
              <div className="text-xs text-[#5c7368]">
                Reposición del <b>{reposEditOpen.fecha}</b> registrada por <b>{reposEditOpen.user_nombre}</b>
              </div>
              <div>
                <Label>Monto (USD)</Label>
                <Input type="number" step="0.01" value={reposEditForm.monto} onChange={(e) => setReposEditForm(f => ({ ...f, monto: e.target.value }))} data-testid="repos-edit-monto" />
              </div>
              <div>
                <Label>Medio de pago origen</Label>
                <Select value={reposEditForm.medio_pago_origen} onValueChange={(v) => setReposEditForm(f => ({ ...f, medio_pago_origen: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="transferencia">Transferencia bancaria</SelectItem>
                    <SelectItem value="efectivo">Efectivo</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Observaciones</Label>
                <Input value={reposEditForm.observaciones} onChange={(e) => setReposEditForm(f => ({ ...f, observaciones: e.target.value }))} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReposEditOpen(null)}>Cancelar</Button>
            <Button onClick={guardarEditReposicion} className="bg-[#205c45]" data-testid="repos-edit-save">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reporte mensual dialog */}
      <Dialog open={reporteOpen} onOpenChange={setReporteOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="caja-reporte-dialog">
          <DialogHeader>
            <DialogTitle>Reporte mensual · Caja Chica</DialogTitle>
            <DialogDescription>Resumen de reposiciones y gastos pagados con caja chica en el mes seleccionado.</DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 items-end mb-4">
            <div>
              <Label className="text-xs">Año</Label>
              <Input type="number" value={repYear} onChange={(e) => setRepYear(parseInt(e.target.value) || today.getFullYear())} className="w-24 h-8" data-testid="caja-rep-year" />
            </div>
            <div>
              <Label className="text-xs">Mes</Label>
              <Select value={String(repMonth)} onValueChange={(v) => setRepMonth(parseInt(v))}>
                <SelectTrigger className="w-40 h-8" data-testid="caja-rep-month"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"].map((m,i) => (
                    <SelectItem key={i+1} value={String(i+1)}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button size="sm" onClick={cargarReporte} disabled={loadingReporte} className="bg-[#205c45] h-8" data-testid="caja-rep-aplicar">
              Aplicar
            </Button>
            {reporte && (
              <Button
                size="sm"
                variant="outline"
                onClick={descargarPdfReporte}
                className="h-8 ml-auto"
                data-testid="caja-rep-pdf"
              >
                <FileDown size={14} className="mr-1" /> Descargar PDF
              </Button>
            )}
          </div>
          {reporte && (
            <div className="space-y-4" data-testid="caja-reporte-body">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="rounded-md bg-white border border-[#d1dbd6] p-3">
                  <div className="text-[10px] uppercase tracking-wider text-[#5c7368] font-semibold">Saldo apertura</div>
                  <div className="text-lg font-bold tabular-nums text-[#15241d]">${reporte.saldo_apertura.toFixed(2)}</div>
                </div>
                <div className="rounded-md bg-white border border-[#d1dbd6] p-3">
                  <div className="text-[10px] uppercase tracking-wider text-[#5c7368] font-semibold">Reposiciones</div>
                  <div className="text-lg font-bold tabular-nums text-[#205c45]">+${reporte.total_reposiciones.toFixed(2)}</div>
                  <div className="text-[10px] text-[#5c7368]">{reporte.n_reposiciones} mov.</div>
                </div>
                <div className="rounded-md bg-white border border-[#d1dbd6] p-3">
                  <div className="text-[10px] uppercase tracking-wider text-[#5c7368] font-semibold">Gastos</div>
                  <div className="text-lg font-bold tabular-nums text-[#b53a25]">−${reporte.total_gastos.toFixed(2)}</div>
                  <div className="text-[10px] text-[#5c7368]">{reporte.n_gastos} mov.</div>
                </div>
                <div className="rounded-md bg-[#e9f2ee] border border-[#b2d0c2] p-3">
                  <div className="text-[10px] uppercase tracking-wider text-[#205c45] font-semibold">Saldo cierre</div>
                  <div className="text-lg font-bold tabular-nums text-[#15241d]" data-testid="caja-rep-saldo-cierre">${reporte.saldo_cierre.toFixed(2)}</div>
                </div>
              </div>

              {reporte.por_categoria.length > 0 && (
                <div className="rounded-md border border-[#d1dbd6] bg-white p-3">
                  <h3 className="text-sm font-bold uppercase tracking-wider text-[#5c7368] mb-2">Por categoría</h3>
                  <table className="w-full text-sm">
                    <thead><tr className="border-b"><th className="text-left py-1">Categoría</th><th className="text-right">Total</th><th className="text-right w-12">N°</th></tr></thead>
                    <tbody>
                      {reporte.por_categoria.map((r, i) => (
                        <tr key={i} className="border-b border-[#e6ebe8]">
                          <td className="py-1">{r.categoria}</td>
                          <td className="text-right tabular-nums font-semibold">${r.total.toFixed(2)}</td>
                          <td className="text-right text-xs text-[#5c7368]">{r.count}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
                <h3 className="text-sm font-bold uppercase tracking-wider text-[#5c7368] px-3 pt-3 pb-2">Movimientos del mes</h3>
                <table className="erp-table">
                  <thead><tr><th>Fecha</th><th>Tipo</th><th>Descripción</th><th>Categoría / Origen</th><th className="text-right">Monto</th></tr></thead>
                  <tbody>
                    {reporte.movimientos.map((m, i) => (
                      <tr key={i} data-testid={`caja-rep-mov-${i}`}>
                        <td className="font-mono text-xs">{m.fecha}</td>
                        <td>
                          {m.tipo === "reposicion" ? (
                            <Badge className="bg-[#e9f2ee] text-[#205c45] border border-[#b2d0c2]">+ Reposición</Badge>
                          ) : (
                            <Badge className="bg-[#fdecea] text-[#b53a25] border border-[#d99584]">- Gasto</Badge>
                          )}
                        </td>
                        <td>{m.descripcion || (m.tipo === "reposicion" ? "Reposición de fondo" : "—")}</td>
                        <td className="text-xs text-[#5c7368]">{m.tipo === "reposicion" ? `Origen: ${m.medio_pago_origen}` : (m.categoria_nombre || "")}</td>
                        <td className={`text-right tabular-nums font-semibold ${m.tipo === "reposicion" ? "text-[#205c45]" : "text-[#b53a25]"}`}>
                          {m.signo}${(+m.monto).toFixed(2)}
                        </td>
                      </tr>
                    ))}
                    {reporte.movimientos.length === 0 && <tr><td colSpan={5} className="text-center py-6 text-[#8ba397]">Sin movimientos en el mes</td></tr>}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setReporteOpen(false)}>Cerrar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ===================== CATEGORIAS =====================
function Categorias() {
  const [items, setItems] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ nombre: "", descripcion: "", activo: true });

  const load = async () => {
    try { const r = await api.get("/gastos/categorias"); setItems(r.data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.nombre) return toast.error("Ingrese nombre");
    try {
      if (editing) await api.patch(`/gastos/categorias/${editing.id}`, form);
      else await api.post("/gastos/categorias", form);
      toast.success(editing ? "Categoría actualizada" : "Categoría creada");
      setDialogOpen(false); setEditing(null); setForm({ nombre: "", descripcion: "", activo: true }); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (c) => {
    if (!window.confirm(`¿Eliminar "${c.nombre}"?`)) return;
    try { await api.delete(`/gastos/categorias/${c.id}`); load(); } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="mt-4 space-y-4">
      <div className="flex justify-end">
        <Button onClick={() => { setEditing(null); setForm({ nombre: "", descripcion: "", activo: true }); setDialogOpen(true); }} className="bg-[#205c45]" data-testid="create-cat-btn">
          <Plus size={16} className="mr-2" /> Nueva categoría
        </Button>
      </div>
      <div className="rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
        <table className="erp-table">
          <thead><tr><th>Nombre</th><th>Descripción</th><th>Estado</th><th className="w-24"></th></tr></thead>
          <tbody>
            {items.map(c => (
              <tr key={c.id}>
                <td className="font-medium">{c.nombre}</td>
                <td className="text-xs text-[#5c7368]">{c.descripcion || "—"}</td>
                <td>{c.activo ? <Badge className="bg-[#e9f2ee] text-[#205c45]">Activa</Badge> : <Badge variant="outline">Inactiva</Badge>}</td>
                <td>
                  <div className="flex gap-1 justify-end">
                    <Button size="icon" variant="ghost" onClick={() => { setEditing(c); setForm({ nombre: c.nombre, descripcion: c.descripcion || "", activo: c.activo }); setDialogOpen(true); }}><Edit2 size={14} /></Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(c)}><Trash2 size={14} className="text-[#b53a25]" /></Button>
                  </div>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={4} className="text-center py-10 text-[#8ba397]">Sin categorías. Cree algunas (Servicios, Papelería, Combustible, etc.)</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>{editing ? "Editar categoría" : "Nueva categoría"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nombre</Label><Input value={form.nombre} onChange={(e) => setForm({ ...form, nombre: e.target.value })} data-testid="cat-nombre" /></div>
            <div><Label>Descripción</Label><Input value={form.descripcion} onChange={(e) => setForm({ ...form, descripcion: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button onClick={save} className="bg-[#205c45]" data-testid="cat-save">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ===================== REPORTE =====================
function Reporte() {
  const today = new Date();
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [data, setData] = useState(null);

  const load = async () => {
    try { const r = await api.get(`/gastos/reporte/mensual?year=${year}&month=${month}`); setData(r.data); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [year, month]);

  return (
    <div className="mt-4 space-y-4">
      <div className="flex gap-3 items-end">
        <div><Label>Año</Label><Input type="number" value={year} onChange={(e) => setYear(parseInt(e.target.value) || today.getFullYear())} className="w-28" /></div>
        <div>
          <Label>Mes</Label>
          <Select value={String(month)} onValueChange={(v) => setMonth(parseInt(v))}>
            <SelectTrigger className="w-40"><SelectValue /></SelectTrigger>
            <SelectContent>
              {["Enero","Febrero","Marzo","Abril","Mayo","Junio","Julio","Agosto","Septiembre","Octubre","Noviembre","Diciembre"].map((m,i) => (
                <SelectItem key={i+1} value={String(i+1)}>{m}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {data && (
        <>
          <div className="grid md:grid-cols-4 gap-3">
            <div className="rounded-md bg-white border border-[#d1dbd6] p-4">
              <div className="text-xs uppercase tracking-wider text-[#5c7368] font-semibold">Total gastos</div>
              <div className="text-2xl font-bold tabular-nums text-[#b53a25]" data-testid="report-total">${data.total_gastos.toFixed(2)}</div>
              <div className="text-[10px] text-[#5c7368]">{data.n_gastos} movimientos</div>
            </div>
            <div className="rounded-md bg-white border border-[#d1dbd6] p-4">
              <div className="text-xs uppercase tracking-wider text-[#5c7368] font-semibold">Reposiciones C.Chica</div>
              <div className="text-2xl font-bold tabular-nums text-[#205c45]">${data.total_reposiciones_caja_chica.toFixed(2)}</div>
              <div className="text-[10px] text-[#5c7368]">{data.n_reposiciones} reposiciones</div>
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="rounded-md border border-[#d1dbd6] bg-white p-3">
              <h3 className="text-sm font-bold uppercase tracking-wider text-[#5c7368] mb-2">Por categoría</h3>
              <table className="w-full text-sm">
                <thead><tr className="border-b"><th className="text-left py-1">Categoría</th><th className="text-right">Total</th><th className="text-right w-12">N°</th></tr></thead>
                <tbody>
                  {data.por_categoria.map((r, i) => (
                    <tr key={i} className="border-b border-[#e6ebe8]">
                      <td className="py-1">{r.categoria}</td>
                      <td className="text-right tabular-nums font-semibold">${r.total.toFixed(2)}</td>
                      <td className="text-right text-xs text-[#5c7368]">{r.count}</td>
                    </tr>
                  ))}
                  {data.por_categoria.length === 0 && <tr><td colSpan={3} className="text-center py-3 text-[#8ba397]">Sin gastos</td></tr>}
                </tbody>
              </table>
            </div>
            <div className="rounded-md border border-[#d1dbd6] bg-white p-3">
              <h3 className="text-sm font-bold uppercase tracking-wider text-[#5c7368] mb-2">Por medio de pago</h3>
              <table className="w-full text-sm">
                <thead><tr className="border-b"><th className="text-left py-1">Medio</th><th className="text-right">Total</th><th className="text-right w-12">N°</th></tr></thead>
                <tbody>
                  {data.por_medio_pago.map((r, i) => (
                    <tr key={i} className="border-b border-[#e6ebe8]">
                      <td className="py-1 capitalize">{r.medio_pago.replace("_", " ")}</td>
                      <td className="text-right tabular-nums font-semibold">${r.total.toFixed(2)}</td>
                      <td className="text-right text-xs text-[#5c7368]">{r.count}</td>
                    </tr>
                  ))}
                  {data.por_medio_pago.length === 0 && <tr><td colSpan={3} className="text-center py-3 text-[#8ba397]">Sin gastos</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
